/**
 * Превью стены, которую пользователь сейчас чертит:
 * полупрозрачное «тело» будущей стены, ось и живая подпись длины.
 *
 * Тело рисуется контуром из `wallDraftOutlines` — со стыком в углу, как встанет стена;
 * без контура (нечего стыковать) — полосой по оси.
 *
 * Цвет говорит, что получится: перегородка синяя, несущая — красная, как и на
 * чертеже. Если стену здесь построить нельзя (пересекла бы другую), ось
 * становится пунктирной и ярко-красной, а сверху висит плашка с причиной.
 */
import {
	add,
	angleOf,
	dist,
	formatAngle,
	formatMillimeters,
	mul,
	sub,
	type Pt,
} from "@/shared/lib/geometry";
import { BEARING_HATCH } from "@/shared/catalog";
import type { WallOutline } from "@/widgets/plan";
import { useLengthUnit } from "@/shared/store";

interface Props {
	from: Pt;
	to: Pt;
	mmPerPx: number;
	/** Толщина будущей стены, мм — тело черновика рисуется в размер */
	thickness: number;
	/** Черновик несущей стены — красный, как она будет на чертеже */
	bearing?: boolean;
	/** Стена недопустима — ось пунктиром */
	invalid?: boolean;
	/** Контур тела со стыками в углах */
	outline?: WallOutline;
}

const PARTITION_COLOR = "#2f6feb";
const INVALID_COLOR = "#d64545";

export const WallDraft = ({
	from,
	to,
	mmPerPx,
	thickness,
	bearing = false,
	invalid = false,
	outline,
}: Props) => {
	const unit = useLengthUnit();
	const length = dist(from, to);
	const color = invalid ? INVALID_COLOR : bearing ? BEARING_HATCH.stroke : PARTITION_COLOR;
	// Подпись — вплотную к телу стены сверху (у вертикальной — слева), а не в середине
	// комнаты, где её закрывают размеры
	const dir = length > 0 ? mul(sub(to, from), 1 / length) : { x: 1, y: 0 };
	const up = dir.x > 1e-9 || (Math.abs(dir.x) <= 1e-9 && dir.y < 0);
	const normal = up ? { x: dir.y, y: -dir.x } : { x: -dir.y, y: dir.x };
	const labelAt = add(mul(add(from, to), 0.5), mul(normal, thickness / 2 + 8 * mmPerPx));
	const label = `${formatMillimeters(length, unit)} · ${formatAngle(angleOf(sub(to, from)))}${
		bearing ? " · несущая" : ""
	}`;
	return (
		<g style={{ pointerEvents: "none" }}>
			{outline ? (
				<polygon
					points={outline.map((p) => `${p.x},${p.y}`).join(" ")}
					fill={color}
					fillOpacity={0.3}
				/>
			) : (
				<line
					x1={from.x}
					y1={from.y}
					x2={to.x}
					y2={to.y}
					stroke={color}
					strokeOpacity={0.3}
					strokeWidth={thickness}
					strokeLinecap="butt"
				/>
			)}
			<line
				x1={from.x}
				y1={from.y}
				x2={to.x}
				y2={to.y}
				stroke={color}
				strokeWidth={mmPerPx}
				strokeDasharray={invalid ? `${6 * mmPerPx} ${4 * mmPerPx}` : undefined}
			/>
			{length > 0 && (
				<text
					x={labelAt.x}
					y={labelAt.y}
					textAnchor={normal.x < -0.5 ? "end" : normal.x > 0.5 ? "start" : "middle"}
					dominantBaseline={normal.y < -0.5 ? "auto" : "middle"}
					fontSize={13 * mmPerPx}
					fill={color}
					style={{ userSelect: "none" }}
				>
					{label}
				</text>
			)}
		</g>
	);
};
